/**
 * writeQueue/db.ts — low-level IndexedDB layer for the editorial write queue.
 *
 * The write queue's job is to durably persist editorial mutation intent
 * (approve / hold / reject / comment / content edit / source rating) in
 * the browser BEFORE the optimistic UI fires, so the work survives tab
 * close, browser crash, network drop, auth expiry, and page reload.
 *
 * This file is the storage primitive — pure CRUD over IndexedDB. Higher
 * layers (queue.ts, sync.ts) build the public API and background sync
 * worker on top of these primitives.
 *
 * Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 1.
 *
 * IndexedDB notes
 * ---------------
 * - Database name: `votd_admin_write_queue`
 * - Schema version: 1
 * - Object store: `mutations`
 * - Key: client-generated UUID (string) on `id`
 * - Indexes:
 *     • `byStatus` on `status` — sync worker scans for `pending` items
 *     • `byNextRetryAt` on `nextRetryAt` — sync worker picks rows whose retry time has passed
 *     • `byTargetRowId` on `targetRowId` — UI's usePendingForRow hook
 *     • `byCreatedAt` on `createdAt` — ordering, housekeeping
 *
 * Failure mode for unsupported environments (Safari private mode, etc.):
 * `openDb()` rejects with a typed error and the caller (queue.ts) surfaces
 * a "durability disabled" warning rather than blocking the admin UI.
 */

export type MutationOp =
  | 'approve_draft'
  | 'reject_draft'
  | 'update_status_held'
  | 'update_status_rejected'
  | 'update_content'
  | 'update_source_rating'
  | 'comment_append'
  | 'agent_reply'
  | 'triage_proposal';

export type MutationStatus = 'pending' | 'inflight' | 'synced' | 'failed_giveup';

export interface MutationRecord {
  /** Client-generated UUID. Set by queue.ts on enqueue. */
  id: string;
  /** Discriminator — drives dispatch.ts routing. */
  op: MutationOp;
  /** Op-specific payload. JSON-serializable. */
  payload: Record<string, unknown>;
  /** The sourced_queue row being mutated. For UI dedup / usePendingForRow. */
  targetRowId: string;
  /** ms since epoch when enqueued. */
  createdAt: number;
  /** Number of sync attempts so far (0 on enqueue). */
  attempts: number;
  /** ms since epoch — sync worker only picks rows where nextRetryAt <= now. */
  nextRetryAt: number;
  /** Last error message from the sync worker, if any. */
  lastError: string | null;
  /** Lifecycle status. */
  status: MutationStatus;
}

export class IndexedDbUnavailableError extends Error {
  constructor(reason: string) {
    super(`IndexedDB unavailable: ${reason}`);
    this.name = 'IndexedDbUnavailableError';
  }
}

const DB_NAME = 'votd_admin_write_queue';
const DB_VERSION = 1;
const STORE = 'mutations';

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open (and lazily upgrade) the queue database. Memoized — subsequent
 * calls return the same connection. Rejects with IndexedDbUnavailableError
 * if the browser doesn't support IndexedDB or blocks it (Safari private
 * browsing, etc.).
 */
export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new IndexedDbUnavailableError('indexedDB global not present'));
      return;
    }

    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(new IndexedDbUnavailableError(`indexedDB.open threw: ${(err as Error).message}`));
      return;
    }

    req.onerror = () => {
      reject(new IndexedDbUnavailableError(`open request errored: ${req.error?.message ?? 'unknown'}`));
      dbPromise = null;
    };

    req.onblocked = () => {
      // Another tab has the DB open at an older version. Surface this —
      // the editor needs to close other admin tabs to unblock.
      reject(new IndexedDbUnavailableError('open blocked by other tab — close other admin tabs'));
      dbPromise = null;
    };

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('byStatus', 'status', { unique: false });
        store.createIndex('byNextRetryAt', 'nextRetryAt', { unique: false });
        store.createIndex('byTargetRowId', 'targetRowId', { unique: false });
        store.createIndex('byCreatedAt', 'createdAt', { unique: false });
      }
    };

    req.onsuccess = () => {
      const db = req.result;
      // Reset memoized promise on close so the next openDb() call
      // re-opens. Closes happen during unload or schema upgrade in
      // another tab.
      db.onclose = () => { dbPromise = null; };
      // Version-change in another tab: close our connection so the other
      // tab can upgrade.
      db.onversionchange = () => { db.close(); dbPromise = null; };
      resolve(db);
    };
  });

  return dbPromise;
}

/** Insert a new mutation record. Throws if the id collides. */
export async function putMutation(record: MutationRecord): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Update an existing mutation record. Overwrites by id. */
export async function updateMutation(record: MutationRecord): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Delete a mutation record by id. No-op if not present. */
export async function deleteMutation(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/** Get a mutation record by id. Resolves to null if not present. */
export async function getMutation(id: string): Promise<MutationRecord | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => resolve((req.result as MutationRecord | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** List all records with a given status. */
export async function listByStatus(status: MutationStatus): Promise<MutationRecord[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const idx = tx.objectStore(STORE).index('byStatus');
    const req = idx.getAll(status);
    req.onsuccess = () => resolve((req.result as MutationRecord[]) ?? []);
    req.onerror = () => reject(req.error);
  });
}

/**
 * List pending records whose nextRetryAt <= now. Used by sync.ts each tick
 * to find ready-to-retry work. Returns oldest-first.
 */
export async function listReadyToRetry(now: number): Promise<MutationRecord[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const idx = tx.objectStore(STORE).index('byStatus');
    const req = idx.getAll('pending');
    req.onsuccess = () => {
      const all = (req.result as MutationRecord[]) ?? [];
      const ready = all.filter(r => r.nextRetryAt <= now);
      ready.sort((a, b) => a.createdAt - b.createdAt);
      resolve(ready);
    };
    req.onerror = () => reject(req.error);
  });
}

/** List all records targeting a given sourced_queue row. */
export async function listByTargetRow(targetRowId: string): Promise<MutationRecord[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const idx = tx.objectStore(STORE).index('byTargetRowId');
    const req = idx.getAll(targetRowId);
    req.onsuccess = () => resolve((req.result as MutationRecord[]) ?? []);
    req.onerror = () => reject(req.error);
  });
}

/** Sum up everything in the queue grouped by status. Cheap, used by usePending. */
export async function countByStatus(): Promise<Record<MutationStatus, number>> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const out: Record<MutationStatus, number> = {
      pending: 0,
      inflight: 0,
      synced: 0,
      failed_giveup: 0,
    };
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        const rec = cursor.value as MutationRecord;
        if (rec.status in out) out[rec.status] += 1;
        cursor.continue();
      } else {
        resolve(out);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Delete all `synced` records older than ageMs. Housekeeping — kept on
 * disk briefly for debugging, then pruned. Called periodically by sync.ts.
 */
export async function pruneSynced(olderThanMs: number): Promise<number> {
  const db = await openDb();
  const cutoff = Date.now() - olderThanMs;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const idx = tx.objectStore(STORE).index('byStatus');
    const req = idx.openCursor(IDBKeyRange.only('synced'));
    let removed = 0;
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        const rec = cursor.value as MutationRecord;
        if (rec.createdAt < cutoff) {
          cursor.delete();
          removed += 1;
        }
        cursor.continue();
      } else {
        resolve(removed);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Check whether IndexedDB is usable in the current environment. Resolves
 * to a status object the caller can use to show a warning banner. Doesn't
 * throw — returns { ok: false, reason: ... } on failure instead.
 *
 * Safari private browsing is the dominant failure mode; this gives a fast
 * answer the UI can render at startup without hanging on the eventual
 * openDb() attempt.
 */
export async function probeAvailability(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (typeof indexedDB === 'undefined') {
    return { ok: false, reason: 'indexedDB global not present' };
  }
  try {
    await openDb();
    return { ok: true };
  } catch (err) {
    if (err instanceof IndexedDbUnavailableError) {
      return { ok: false, reason: err.message };
    }
    return { ok: false, reason: (err as Error).message };
  }
}
